import { db } from '@/firebase';
import LeadsService from '@/services/server/LeadsService';
import UsersServiceFirebase from '@/services/firebase/Users/UsersService';

/**
 * Busca todas as propostas de todas as fontes
 * @param {string|null} idFranchisee - ID do franqueado (opcional, se null busca todas)
 * @returns {Promise<Array>} Array de propostas com informações enriquecidas
 */
async function getAllProposals(idFranchisee = null) {
  try {
    const allProposals = [];

    // Buscar propostas de diagnostic_proposals (ProjectsKits)
    // Buscar diretamente do Firebase para incluir id_doc
    let diagnosticProposals = [];
    try {
      let query = db.collection('diagnostic_proposals').where('origin', '==', 'projects_kits');
      if (idFranchisee) {
        query = query.where('id_franchisee', '==', idFranchisee);
      }
      const snapshot = await query.get();
      snapshot.forEach((doc) => {
        diagnosticProposals.push({
          ...doc.data(),
          id_doc: doc.id,
        });
      });
    } catch (error) {
      console.error('Error fetching diagnostic proposals:', error);
    }

    // Adicionar tipo de origem
    diagnosticProposals.forEach((proposal) => {
      allProposals.push({
        ...proposal,
        proposalSource: 'diagnostic_proposals',
        proposalType: proposal.origin === 'projects_kits' ? 'ProjectsKits' : 'ProjectsKits',
        // Garantir que a tag origin esteja presente
        origin: proposal.origin || 'projects_kits'
      });
    });

    return allProposals;
  } catch (error) {
    console.error('Error getting all proposals:', error);
    throw new Error('getAllProposals Fail!' + error);
  }
}

/**
 * Enriquece as propostas com informações de lead e franqueado
 * @param {Array} proposals - Array de propostas
 * @returns {Promise<Array>} Array de propostas enriquecidas
 */
async function enrichProposalsWithLeadAndFranchisee(proposals) {
  try {
    // Coletar IDs únicos de leads e franqueados
    const leadIds = [...new Set(proposals.map(p => p.id_lead).filter(Boolean))];
    const franchiseeIds = [...new Set(proposals.map(p => p.id_franchisee).filter(Boolean))];

    // Buscar informações de leads e franqueados em paralelo
    const leadPromises = leadIds.map(id => LeadsService.getLeadById(id));
    const franchiseePromises = franchiseeIds.map(id => UsersServiceFirebase.getUsersById(id));

    const leadResults = await Promise.all(leadPromises);
    const franchiseeResults = await Promise.all(franchiseePromises);

    // Criar mapas para acesso rápido
    const leadsMap = new Map();
    leadResults.forEach(([status, lead], index) => {
      if (status && lead) {
        leadsMap.set(leadIds[index], lead);
      }
    });

    const franchiseesMap = new Map();
    franchiseeResults.forEach((franchisee, index) => {
      if (franchisee) {
        franchiseesMap.set(franchiseeIds[index], franchisee);
      }
    });

    // Enriquecer propostas
    return proposals.map(proposal => {
      const lead = leadsMap.get(proposal.id_lead);
      const franchisee = franchiseesMap.get(proposal.id_franchisee);

      return {
        ...proposal,
        lead: lead || null,
        leadName: lead?.name || 'N/A',
        leadDocument: lead?.document || 'N/A',
        franchisee: franchisee || null,
        franchiseeName: franchisee?.name || 'N/A',
      };
    });
  } catch (error) {
    console.error('Error enriching proposals:', error);
    return proposals; // Retornar propostas sem enriquecimento em caso de erro
  }
}

export default {
  getAllProposals,
  enrichProposalsWithLeadAndFranchisee,
};

